import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { threatMonitor, SecurityEvent, SecurityMetrics } from '@services/security/threatMonitor';
import { mfaClient } from '@services/auth/mfaClient';
import { MFAStatus } from '../../types/auth/mfa';
import { cspManager } from '@services/security/cspManager';
import { securityHeaders } from '@services/security/securityHeaders';

export interface SecurityContextState {
  // Threat monitoring
  recentEvents: SecurityEvent[];
  securityMetrics: SecurityMetrics;
  
  // MFA status
  mfaStatus: MFAStatus | null;
  
  // CSP information
  currentNonce: string;
  cspViolations: number;
  
  // Security headers status
  headersEnabled: boolean;
  
  // Overall security status
  securityLevel: 'low' | 'medium' | 'high' | 'critical';
  activeThreats: number;
  
  // System status
  isInitialized: boolean;
  lastUpdate: number;
}

export interface SecurityContextActions {
  // Event management
  logSecurityEvent: (event: Omit<SecurityEvent, 'id' | 'timestamp' | 'resolved'>) => string;
  resolveEvent: (eventId: string, resolvedBy: string) => boolean;
  clearEvents: () => void;
  
  // MFA actions
  refreshMFAStatus: (userId?: string) => Promise<void>;
  
  // CSP actions
  getCurrentNonce: () => string;
  forceNonceRotation: () => string;
  
  // Security monitoring
  refreshSecurityStatus: () => void;
  
  // Threat pattern detection
  checkForThreats: () => void;
}

export interface SecurityProviderProps {
  children: ReactNode;
  userId?: string;
  autoRefresh?: boolean;
  refreshInterval?: number;
  maxEvents?: number;
}

const SecurityContext = createContext<{
  state: SecurityContextState;
  actions: SecurityContextActions;
} | null>(null);

export const SecurityProvider: React.FC<SecurityProviderProps> = ({
  children,
  userId,
  autoRefresh = true,
  refreshInterval = 5000,
  maxEvents = 100
}) => {
  const [state, setState] = useState<SecurityContextState>(() => ({
    recentEvents: [],
    securityMetrics: {
      totalEvents: 0,
      eventsByType: {},
      eventsBySeverity: {},
      activeThreats: 0,
      resolvedThreats: 0,
      averageResolutionTime: 0,
      topSources: []
    },
    mfaStatus: null,
    currentNonce: cspManager.getCurrentNonce(),
    cspViolations: 0,
    headersEnabled: true,
    securityLevel: 'medium',
    activeThreats: 0,
    isInitialized: false,
    lastUpdate: Date.now()
  }));

  // Initialize security context
  useEffect(() => {
    const initialize = async () => {
      try {
        // Get initial data
        const events = threatMonitor.getEvents({ limit: maxEvents });
        const metrics = threatMonitor.getMetrics();
        const mfaStatus = userId ? await mfaClient.getStatus(userId) : null;
        const headersStats = securityHeaders.getStatistics();
        
        setState(prevState => ({
          ...prevState,
          recentEvents: events,
          securityMetrics: metrics,
          mfaStatus,
          headersEnabled: headersStats.enabledHeaders.length > 0,
          securityLevel: calculateSecurityLevel(metrics, events),
          activeThreats: metrics.activeThreats,
          isInitialized: true,
          lastUpdate: Date.now()
        }));

      } catch (error) {
        console.error('Failed to initialize security context:', error);
        threatMonitor.logEvent({
          type: 'system_error',
          severity: 'high',
          source: 'security_provider',
          details: {
            action: 'initialization_failed',
            error: error instanceof Error ? error.message : 'Unknown error'
          }
        });
      }
    };

    initialize();
  }, [userId, maxEvents]);

  // Setup event listener for security events
  useEffect(() => {
    const eventSubscriberId = `security-provider-${Date.now()}`;
    
    threatMonitor.subscribe(eventSubscriberId, (event) => {
      setState(prevState => {
        const newEvents = [event, ...prevState.recentEvents].slice(0, maxEvents);
        const newMetrics = threatMonitor.getMetrics();
        
        return {
          ...prevState,
          recentEvents: newEvents,
          securityMetrics: newMetrics,
          securityLevel: calculateSecurityLevel(newMetrics, newEvents),
          activeThreats: newMetrics.activeThreats,
          lastUpdate: Date.now()
        };
      });
    });

    return () => {
      threatMonitor.unsubscribe(eventSubscriberId);
    };
  }, [maxEvents]);

  // Setup nonce rotation observer
  useEffect(() => {
    const nonceObserverId = `security-provider-nonce-${Date.now()}`;
    
    cspManager.subscribeToNonceRotation(nonceObserverId, (newNonce) => {
      setState(prevState => ({
        ...prevState,
        currentNonce: newNonce,
        lastUpdate: Date.now()
      }));
    });

    return () => {
      cspManager.unsubscribeFromNonceRotation(nonceObserverId);
    };
  }, []);

  // Setup auto-refresh timer
  useEffect(() => {
    if (!autoRefresh) return;

    const timer = setInterval(() => {
      const metrics = threatMonitor.getMetrics();
      const events = threatMonitor.getEvents({ limit: maxEvents });
      
      setState(prevState => ({
        ...prevState,
        securityMetrics: metrics,
        recentEvents: events,
        securityLevel: calculateSecurityLevel(metrics, events),
        activeThreats: metrics.activeThreats,
        lastUpdate: Date.now()
      }));
    }, refreshInterval);

    return () => clearInterval(timer);
  }, [autoRefresh, refreshInterval, maxEvents]);

  // Actions implementation
  const logSecurityEvent = (event: Omit<SecurityEvent, 'id' | 'timestamp' | 'resolved'>): string => {
    return threatMonitor.logEvent(event);
  };

  const resolveEvent = (eventId: string, resolvedBy: string): boolean => {
    return threatMonitor.resolveEvent(eventId, resolvedBy);
  };

  const clearEvents = (): void => {
    setState(prevState => ({
      ...prevState,
      recentEvents: [],
      lastUpdate: Date.now()
    }));
  };

  const refreshMFAStatus = async (targetUserId?: string): Promise<void> => {
    const userIdToCheck = targetUserId || userId;
    if (userIdToCheck) {
      try {
        const mfaStatus = await mfaClient.getStatus(userIdToCheck);
        setState(prevState => ({
          ...prevState,
          mfaStatus,
          lastUpdate: Date.now()
        }));
      } catch (error) {
        console.error('Failed to refresh MFA status:', error);
      }
    }
  };

  const getCurrentNonce = (): string => {
    return cspManager.getCurrentNonce();
  };

  const forceNonceRotation = (): string => {
    return cspManager.forceNonceRotation();
  };

  const refreshSecurityStatus = (): void => {
    const events = threatMonitor.getEvents({ limit: maxEvents });
    const metrics = threatMonitor.getMetrics();
    const headersStats = securityHeaders.getStatistics();
    
    setState(prevState => ({
      ...prevState,
      recentEvents: events,
      securityMetrics: metrics,
      headersEnabled: headersStats.enabledHeaders.length > 0,
      securityLevel: calculateSecurityLevel(metrics, events),
      activeThreats: metrics.activeThreats,
      lastUpdate: Date.now()
    }));
  };

  const checkForThreats = (): void => {
    const patterns = threatMonitor.getThreatPatterns();
    const activePatterns = patterns.filter(p => p.status === 'active');
    
    setState(prevState => ({
      ...prevState,
      activeThreats: activePatterns.length,
      lastUpdate: Date.now()
    }));
  };

  const actions: SecurityContextActions = {
    logSecurityEvent,
    resolveEvent,
    clearEvents,
    refreshMFAStatus,
    getCurrentNonce,
    forceNonceRotation,
    refreshSecurityStatus,
    checkForThreats
  };

  return (
    <SecurityContext.Provider value={{ state, actions }}>
      {children}
    </SecurityContext.Provider>
  );
};

// Hook to use security context
export const useSecurity = () => {
  const context = useContext(SecurityContext);
  if (!context) {
    throw new Error('useSecurity must be used within a SecurityProvider');
  }
  return context;
};

// Helper function to calculate security level
function calculateSecurityLevel(metrics: SecurityMetrics, events: SecurityEvent[]): 'low' | 'medium' | 'high' | 'critical' {
  const recentCriticalEvents = events.filter(e => 
    e.severity === 'critical' && 
    Date.now() - e.timestamp < 300000 // Last 5 minutes
  ).length;

  const recentHighEvents = events.filter(e => 
    e.severity === 'high' && 
    Date.now() - e.timestamp < 600000 // Last 10 minutes
  ).length;

  // Critical level: any recent critical events or many high events
  if (recentCriticalEvents > 0 || recentHighEvents > 5 || metrics.activeThreats > 3) {
    return 'critical';
  }

  // High level: recent high events or active threats
  if (recentHighEvents > 2 || metrics.activeThreats > 1) {
    return 'high';
  }

  // Medium level: some recent events
  const recentEvents = events.filter(e => Date.now() - e.timestamp < 900000).length; // Last 15 minutes
  if (recentEvents > 10 || metrics.activeThreats > 0) {
    return 'medium';
  }

  return 'low';
}

export default SecurityProvider;